// Room DATA (roomState/myPlayer/myTeam) is NOT held here — it lives in the
// tearing-safe external store (roomStateStore) and is read via useSyncExternalStore.
// This context only carries actions + low-frequency lifecycle flags, so it can be
// a plain React context.
//
// A state sync always sends events, never a snapshot, so the client rebuilds
// state through the same path as live play. With no state yet, the sync retries
// for up to 15 seconds: creating a room runs a long chain of server events, and
// a new room's state can lag 2 to 4 seconds behind the connection.
//
// An event more than 3 seconds old counts as replayed and keeps the connection
// overlay up, so players don't watch every past game phase go by. Live events
// arrive within a few hundred milliseconds, and replayed ones keep their original
// timestamps, minutes or more old, so 3 seconds splits them cleanly.
//
// Handlers register with the parents once, as stable wrappers that call refs
// updated on each render, so they never capture stale state.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import RoomConnectionOverlay from '@/components/room/RoomConnectionOverlay';
// Note: Import hooks directly (not from "../hooks") to avoid cycles.
import { useRoomSocket } from '../hooks/useRoomSocket';
import { useRoute } from '../hooks/useRoute';
import { useAuth } from '../hooks/useAuth';
import { usePlayerInitialization } from '../hooks/usePlayerInitialization';
import { getOrCreateSessionId } from '../utilities/cookies';
import { applyEvent } from '@airhead-games/shared/engine/roomEventApplier';
import {
  setRoomStoreRoomState,
  setRoomStoreMyPlayer,
  setRoomStoreMyTeam,
  getRoomStoreRoomState,
  getRoomStoreMyPlayer,
  useRoomStoreRoomState,
  useRoomStoreMyPlayer,
  useRoomStoreMyTeam,
} from './roomStateStore';
import {
  RoomState,
  RoomPlayer,
  PlatformEvent,
  EventMessage,
  QueryType,
  SenderRoleType,
  RoomTeam,
} from '@airhead-games/shared/types';
import { createLogger, generateQueryId } from '@airhead-games/shared/utilities';

const logger = createLogger('RoomStateContext');
export interface RoomStateContextType {
  // Player initialization state (for playing routes)
  isPlayerInitializing: boolean;
  playerInitializationError: string | null;

  // Event replay detection state
  isReplayingEvents: boolean;

  // Core actions
  applyRoomEvent: (event: PlatformEvent) => void;
  setMyPlayer: (player: RoomPlayer | null) => void;
  setMyTeam: (team: RoomTeam | null) => void;
  clearRoomState: () => void;

  // Development-only actions
  setDevRoomState: (state: RoomState) => void;

  // Event forwarding (for child MediaProvider)
  registerMediaEventHandler: (handler: (event: PlatformEvent) => void) => void;
  registerRoomClosedHandler: (
    handler: (event: PlatformEvent & { type: 'RoomClosed' }) => void
  ) => void;
}

export const RoomStateContext =
  React.createContext<RoomStateContextType | null>(null);

interface RoomStateProviderProps {
  children: React.ReactNode;
}

export const RoomStateProvider: React.FC<RoomStateProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { setLastRoomAccent } = useAuth();

  // Room data is owned by the external store (single source of truth). Read it
  // reactively here so the provider's own effects (player init, team sync,
  // accent) re-run when it changes.
  const roomState = useRoomStoreRoomState();
  const myPlayer = useRoomStoreMyPlayer();
  const myTeam = useRoomStoreMyTeam();

  const [hasVersionGap, setHasVersionGap] = useState<number | null>(null);
  const [shouldSyncOnConnection, setShouldSyncOnConnection] = useState(false);
  const [isReplayingEvents, setIsReplayingEvents] = useState(false);
  const replayResetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [hasRetryTimedOut, setHasRetryTimedOut] = useState(false);

  const hasRoomStateRef = useRef(false);

  // Stable setters over the store (used by the context value, the player-init
  // callback, and the team-sync effect).
  const setMyPlayer = useCallback((player: RoomPlayer | null) => {
    setRoomStoreMyPlayer(player);
  }, []);
  const setMyTeam = useCallback((team: RoomTeam | null) => {
    setRoomStoreMyTeam(team);
  }, []);

  // Callback refs for event forwarding to MediaProvider
  const mediaEventCallbackRef = useRef<((event: PlatformEvent) => void) | null>(
    null
  );
  const roomClosedCallbackRef = useRef<
    ((event: PlatformEvent & { type: 'RoomClosed' }) => void) | null
  >(null);

  // Refs for stable handler registration
  const roomEventHandlerRef = useRef<
    ((payload: EventMessage['payload']) => void) | null
  >(null);
  const {
    registerRoomEventHandler,
    registerCleanupCallback,
    sendQuery,
    isConnectionReady,
    retryConnection,
  } = useRoomSocket();
  const { roomCode: urlRoomCode, isPlayingRoute } = useRoute();

  // Initialize player only on playing routes (both /player and /playing routes need player data)
  const {
    isPlayerInitializing,
    playerInitializationError,
    playerInitializationFailed,
    reinitializePlayer,
  } = usePlayerInitialization({
    shouldInitializePlayer: isPlayingRoute && roomState !== null,
    onPlayerLoaded: setMyPlayer,
    sessionId: getOrCreateSessionId(),
    roomPlayers: roomState ? Object.values(roomState.players) : [],
    twitchPlayersOnly: roomState?.host.modSettings.twitchPlayersOnly || false,
  });

  const handleRetryRoomConnection = useCallback(() => {
    retryConnection();
    reinitializePlayer();
  }, [retryConnection, reinitializePlayer]);

  // Helper to get expected version from current state
  const getExpectedVersion = useCallback((currentState: RoomState | null) => {
    if (!currentState) return 0; // Fresh room starts at version 0
    return currentState.lastEventVersion + 1;
  }, []);

  // Helper to check if an event is a replay (for analytics tracking)
  const isReplayEvent = (event: PlatformEvent): boolean => {
    const serverTimestamp = event.serverTimestamp;
    return (
      typeof serverTimestamp === 'number' && Date.now() - serverTimestamp > 3000 // 3000ms as threshold for replay
    );
  };

  // Apply a single event to update state. Reads/writes the external store
  // imperatively (no functional setState), so sequential events in a batch each
  // see the latest committed state without stale-closure hazards.
  const applyRoomEvent = useCallback(
    (event: PlatformEvent) => {
      // Classify events as replayed when their server timestamp is stale.
      const serverTimestamp = event.serverTimestamp;
      if (replayResetTimerRef.current) {
        clearTimeout(replayResetTimerRef.current);
        replayResetTimerRef.current = null;
      }

      // Calculate replay status once for use in replay detection and analytics
      const isReplay = isReplayEvent(event);

      if (typeof serverTimestamp === 'number') {
        if (isReplay) {
          setIsReplayingEvents(true);
          replayResetTimerRef.current = setTimeout(() => {
            setIsReplayingEvents(false);
            replayResetTimerRef.current = null;
          }, 1000);
        } else {
          setIsReplayingEvents(false);
        }
      } else {
        setIsReplayingEvents(false);
      }

      // Debug logging to track event versions - build descriptive event string
      let eventDescription = event.type;

      // Append elementType if present (GameElement and InteractionWindow events)
      if ('elementType' in event && event.elementType) {
        eventDescription += `-${event.elementType}`;
      }

      // Append infoType if present (InfoElement events)
      if ('infoType' in event && event.infoType) {
        eventDescription += `-${event.infoType}`;
      }

      // Append mediaType if present (Media events)
      if ('mediaType' in event && event.mediaType) {
        eventDescription += `-${event.mediaType}`;
      }

      logger.debug(
        `Applying event ${eventDescription} (version ${event.version ?? 'UNDEFINED'})...`
      );

      const currentState = getRoomStoreRoomState();

      // When room state is null, handle initial state logic
      if (!currentState) {
        if (event.type === 'RoomCreated') {
          logger.debug('Creating initial room state...');
          try {
            const newState = applyEvent(null, event);
            hasRoomStateRef.current = true;
            setRoomStoreRoomState(newState);
            logger.debug('Room state created successfully');
          } catch (error) {
            logger.error('Failed to create initial room state:', error);
          }
        } else {
          logger.debug(
            `Ignored event while waiting for state to be established`
          );
        }
        return;
      }

      const expectedVersion = getExpectedVersion(currentState);

      // Check for version gap
      if (event.version !== expectedVersion) {
        if (event.version > expectedVersion) {
          logger.warn(
            `Version gap detected. Expected ${expectedVersion}, got ${event.version}. Requesting missing events...`
          );
          setHasVersionGap(currentState.lastEventVersion);
        } else {
          logger.warn(
            `Received old event version ${event.version}, current is ${currentState.lastEventVersion}. Ignored.`
          );
        }
        return;
      }

      // Version is correct, apply the event
      try {
        const newState = applyEvent(currentState, event);
        logger.debug(`Applied event successfully (version ${event.version})`);
        setRoomStoreRoomState(newState);

        // Sync myPlayer if this event affects my player
        const currentMyPlayer = getRoomStoreMyPlayer();
        if (currentMyPlayer?.playerId) {
          let shouldSyncMyPlayer = false;

          // Check if event has a player field that matches myPlayer
          if ('player' in event && event.player) {
            shouldSyncMyPlayer =
              event.player.playerId === currentMyPlayer.playerId;
          }

          // Check if ScoreElement affects myPlayer
          if (event.type === 'ScoreElement' && 'content' in event) {
            shouldSyncMyPlayer = currentMyPlayer.playerId in event.content;
          }

          // Update myPlayer with fresh data from new state
          if (
            shouldSyncMyPlayer &&
            newState.players[currentMyPlayer.playerId]
          ) {
            setRoomStoreMyPlayer(newState.players[currentMyPlayer.playerId]);
            logger.debug(`Synced myPlayer with fresh data from new state`);
          }
        }
      } catch (error) {
        logger.error(`Failed to apply ${event.type} event:`, error);
        // If there's still an error after version validation, request state sync
        setHasVersionGap(currentState.lastEventVersion);
      }
    },
    [setHasVersionGap, getExpectedVersion]
  );

  // Clear local room state
  const clearRoomState = useCallback(() => {
    setRoomStoreRoomState(null);
    setRoomStoreMyPlayer(null);
    setRoomStoreMyTeam(null);
    hasRoomStateRef.current = false;
    setHasVersionGap(null);
    setShouldSyncOnConnection(false);

    // Clear event replay detection state
    setIsReplayingEvents(false);
    if (replayResetTimerRef.current) {
      clearTimeout(replayResetTimerRef.current);
      replayResetTimerRef.current = null;
    }

    // Clear retry timeout state
    setHasRetryTimedOut(false);

    logger.info('Cleared room state successfully');
  }, []);

  // Handle events as they come in from websocket (now supports batched events)
  const handleEvent = useCallback(
    async (payload: EventMessage['payload']) => {
      const events = payload.events as PlatformEvent[];
      // Process each event in the batch sequentially
      for (const event of events) {
        // Forward media events to MediaProvider for playback handling
        if (
          event.type === 'MediaRequested' ||
          event.type === 'MediaReady' ||
          event.type === 'MediaStarted' ||
          event.type === 'MediaCompleted'
        ) {
          if (mediaEventCallbackRef.current) {
            mediaEventCallbackRef.current(event);
          }
        }

        // Handle RoomClosed events for cleanup (navigation handled by RoomSocketContext)
        if (event.type === 'RoomClosed') {
          logger.debug('Processing RoomClosed event: Clearing room state...');

          // Forward to MediaProvider first
          if (roomClosedCallbackRef.current) {
            roomClosedCallbackRef.current(
              event as PlatformEvent & { type: 'RoomClosed' }
            );
          }

          // Clear local room state
          clearRoomState();
          return; // Don't apply RoomClosed to room state
        }

        // Handle being kicked from the room on playing routes.
        const currentMyPlayer = getRoomStoreMyPlayer();
        if (
          isPlayingRoute &&
          event.type === 'PlayerRemoved' &&
          event.reason === 'kicked' &&
          currentMyPlayer?.playerId &&
          event.player.playerId === currentMyPlayer.playerId
        ) {
          logger.info(
            `[${event.roomCode}] Current player was kicked, redirecting to join page...`
          );
          clearRoomState();
          navigate('/join');
          return;
        }

        // Apply ALL events (including media events) to room state
        applyRoomEvent(event);
      }
    },
    [applyRoomEvent, clearRoomState, isPlayingRoute, navigate]
  );

  // Update handler refs with current handlers
  roomEventHandlerRef.current = handleEvent;

  // Register stable handler ref with RoomSocketProvider (once)
  useEffect(() => {
    logger.debug('Registering stable handlers with RoomSocketContext...');
    registerRoomEventHandler((payload) => {
      if (roomEventHandlerRef.current) {
        roomEventHandlerRef.current(payload);
      }
    });
  }, [registerRoomEventHandler]);

  // Register cleanup callback for navigation mismatch scenarios
  useEffect(() => {
    return registerCleanupCallback(() => {
      logger.debug(
        'Processing room mismatch: Clearing room state only (no navigation)...'
      );
      clearRoomState();
    });
  }, [registerCleanupCallback, clearRoomState]);

  // Detect when we are finally ready and connected to a room (works for first connection or reconnection)
  useEffect(() => {
    if (isConnectionReady) {
      setShouldSyncOnConnection(true);
    } else {
      setShouldSyncOnConnection(false);
    }
  }, [isConnectionReady]);

  // Execute state sync when any flag is triggered
  useEffect(() => {
    if (shouldSyncOnConnection || hasVersionGap) {
      if (!urlRoomCode) {
        logger.warn('No room code found, skipping state sync...');
        return;
      }

      if (!isConnectionReady) {
        logger.debug('Connection not ready, skipping state sync...');
        return;
      }

      const requestStateSync = () => {
        const lastEventVersion = shouldSyncOnConnection
          ? -1 // Full sync when connection is ready
          : (hasVersionGap ?? -1); // Use the actual version gap number

        logger.debug(
          `Requesting state sync with lastEventVersion: ${lastEventVersion}`
        );
        sendQuery({
          id: generateQueryId(),
          type: QueryType.STATE_SYNC,
          senderRole: isPlayingRoute
            ? SenderRoleType.PLAYER
            : SenderRoleType.HOST,
          clientTimestamp: Date.now(),
          roomCode: urlRoomCode,
          lastEventVersion,
          syncType: 'AUTO',
        });
      };

      // Send immediate request
      requestStateSync();

      // Set up retry mechanism if we don't have room state
      if (!hasRoomStateRef.current) {
        const retryInterval = setInterval(() => {
          if (hasRoomStateRef.current) {
            logger.debug('Room state established, clearing retry interval');
            clearInterval(retryInterval);
            setShouldSyncOnConnection(false);
            setHasVersionGap(null);
          } else {
            logger.debug('Retrying state sync request...');
            requestStateSync();
          }
        }, 1000); // More aggressive 1s retries

        // Cleanup interval after 15 seconds to prevent infinite retries
        const maxRetryTimeout = setTimeout(() => {
          logger.warn('Stopping state sync retries after 15 seconds');
          clearInterval(retryInterval);
          setHasRetryTimedOut(true); // Mark that retries have timed out
        }, 15000);

        return () => {
          clearInterval(retryInterval);
          clearTimeout(maxRetryTimeout);
        };
      } else {
        // We have room state, reset flags
        logger.debug('Room state already established, resetting sync flags');
        setShouldSyncOnConnection(false);
        setHasVersionGap(null);
        setHasRetryTimedOut(false);
      }
    }
  }, [
    shouldSyncOnConnection,
    hasVersionGap,
    sendQuery,
    isPlayingRoute,
    urlRoomCode,
    isConnectionReady,
  ]);

  // Sync myTeam whenever roomState.teams or myPlayer.teamId changes
  // Catches all team data changes (direct team events, player score changes, etc.)
  useEffect(() => {
    if (myPlayer?.teamId && roomState?.teams) {
      const team = roomState.teams[myPlayer.teamId];
      if (team) {
        setMyTeam(team);
      } else if (myTeam) {
        setMyTeam(null);
        logger.debug(
          `Cleared myTeam - team ${myPlayer.teamId} no longer exists in room state`
        );
      }
    } else if (!myPlayer?.teamId && myTeam) {
      setMyTeam(null);
    }
  }, [roomState?.teams, myPlayer?.teamId, myTeam, setMyTeam]);

  // Set room accent once when room is established (for player's room state messages outside of room)
  useEffect(() => {
    if (roomState?.host?.userPreferences?.accent) {
      setLastRoomAccent(roomState.host.userPreferences.accent);
    }
  }, [roomState?.host?.userPreferences?.accent, setLastRoomAccent]);

  // Registration functions for event forwarding to MediaProvider
  const registerMediaEventHandler = useCallback(
    (handler: (event: PlatformEvent) => void) => {
      mediaEventCallbackRef.current = handler;
    },
    []
  );

  const registerRoomClosedHandler = useCallback(
    (handler: (event: PlatformEvent & { type: 'RoomClosed' }) => void) => {
      roomClosedCallbackRef.current = handler;
    },
    []
  );

  // Development-only method to inject room state directly
  const setDevRoomState = useCallback((state: RoomState) => {
    if (!import.meta.env.DEV) {
      logger.warn('setDevRoomState is only available in development mode');
      return;
    }

    logger.info('Injecting dev room state directly');
    setRoomStoreRoomState(state);
    hasRoomStateRef.current = true;

    // Reset sync flags since we have state now
    setShouldSyncOnConnection(false);
    setHasVersionGap(null);
    setHasRetryTimedOut(false);
  }, []);

  const value = React.useMemo(
    () => ({
      isPlayerInitializing,
      playerInitializationError,
      isReplayingEvents,
      applyRoomEvent,
      setMyPlayer,
      setMyTeam,
      clearRoomState,
      setDevRoomState,
      registerMediaEventHandler,
      registerRoomClosedHandler,
    }),
    [
      isPlayerInitializing,
      playerInitializationError,
      isReplayingEvents,
      applyRoomEvent,
      setMyPlayer,
      setMyTeam,
      clearRoomState,
      setDevRoomState,
      registerMediaEventHandler,
      registerRoomClosedHandler,
    ]
  );

  return (
    <RoomStateContext.Provider value={value}>
      {children}
      <RoomConnectionOverlay
        hasVersionGap={hasVersionGap}
        shouldSyncOnConnection={shouldSyncOnConnection}
        isPlayerInitializing={isPlayerInitializing}
        playerInitializationFailed={playerInitializationFailed}
        isReplayingEvents={isReplayingEvents}
        isAwaitingRoomState={!roomState}
        hasMaxRetryTimeout={hasRetryTimedOut}
        onRetryRoomConnection={handleRetryRoomConnection}
        onClearRoomState={clearRoomState}
      />
    </RoomStateContext.Provider>
  );
};
